import {bootAuthLayout} from '@scripts/boot';
import {Timer, Toasts, onEnter, sendMetrics, redirect} from '@scripts/helpers';
import {BooleanRule, PassRule} from '@scripts/validation/rules';
import {phonePreset, fioPreset, emailPreset, passPreset, fillPreset} from '@scripts/validation/presets';
import {Validator} from '@scripts/validation/Validator';
import {Gate} from '@scripts/validation/Gate';
import {Auth} from '@scripts/http/api';
import {datePreset} from "~/js/validation/presets";

const auth = new Auth();

function initMetrics(componentStore) {
    if (!METRICS_ENABLE) return;

    const {
        sendSmsCodeButton,
        continueRegisterButton,
        registerButton,
        loginLink,
        successRegisterCollection,
        registrEmailButton,
    } = componentStore.all();

    sendSmsCodeButton.on('click', () => {
        sendMetrics({
            type: 'reachGoal',
            eventID: 'reg_get_code',
            eventCategory: 'button',
        });
    });

    loginLink.on('click', () => {
        sendMetrics({
            type: 'reachGoal',
            eventID: 'reg_enter',
            eventCategory: 'button',
        });
    });

    continueRegisterButton.on('click', () => {
        sendMetrics({
            type: 'reachGoal',
            eventID: 'reg_continue',
            eventCategory: 'button',
        });
    });

    registerButton.on('click', () => {
        sendMetrics({
            type: 'reachGoal',
            eventID: 'reg_finish',
            eventCategory: 'button',
        });
    });

    registrEmailButton.on('click', () => {
        sendMetrics({
            type: 'reachGoal',
            eventID: 'reg_continue',
            eventCategory: 'button',
        });
    });
}

function initValidation(componentStore) {
    const {
        firstNameInput,
        lastNameInput,
        middleNameInput,
        registerButton,
        phoneInputForEmail,
        emailInputForPhone,
        companyInput,
        positionInput,
        birthdayInput
    } = componentStore.all();

    const firstNameValidator = new Validator()
        .required()
        .rule(fioPreset)
        .formField(firstNameInput)
        .getValue(() => firstNameInput.getValue())
        .addEvent('blur')
        .compile();

    const lastNameValidator = new Validator()
        .required()
        .rule(fioPreset)
        .formField(lastNameInput)
        .getValue(() => lastNameInput.getValue())
        .addEvent('blur')
        .compile();

    const middleNameValidator = new Validator()
        .required()
        .rule(fioPreset)
        .formField(middleNameInput)
        .getValue(() => middleNameInput.getValue())
        .addEvent('blur')
        .compile();

    const birthdayValidator = new Validator()
        .required()
        .rule(datePreset)
        .formField(birthdayInput)
        .getValue(birthdayInput.inputmask)
        .addEvent('blur')
        .compile()

    const phoneForEmailValidator = new Validator()
        .required()
        .rule(phonePreset)
        .formField(phoneInputForEmail)
        .getValue(phoneInputForEmail.inputmask)
        .addEvent('blur')
        .compile();

    const emailForPhoneValidator = new Validator()
        .required()
        .rule(emailPreset)
        .formField(emailInputForPhone)
        .getValue(() => emailInputForPhone.getValue())
        .addEvent('blur')
        .compile();

    const companyValidator = new Validator()
        .required()
        .rule(fillPreset)
        .formField(companyInput)
        .getValue(() => companyInput.getValue())
        .addEvent('blur')
        .compile();

    const positionValidator = new Validator()
        .required()
        .rule(fillPreset)
        .formField(positionInput)
        .getValue(() => positionInput.getValue())
        .addEvent('blur')
        .compile();

    const gate = new Gate()
        .addValidator('firstName', firstNameValidator)
        .addValidator('lastName', lastNameValidator)
        .addValidator('middleName', middleNameValidator)
        .addValidator('emailForPhone', emailForPhoneValidator)
        .addValidator('phoneForEmail', phoneForEmailValidator)
        .addValidator('company', companyValidator)
        .addValidator('position', positionValidator)
        .addValidator('birthday', birthdayValidator)
        .onValid(() => registerButton.enable())
        .onInvalid(() => registerButton.disable());

    gate.disable('emailForPhone');

    return gate;
}

function initValidationEmailFields(componentStore) {
    const {
        emailInput,
        inputPassword,
        inputPasswordConfirm,
        registrEmailButton,
        personalData,
        personalPolicy,
    } = componentStore.all();

    const emailValidator = new Validator()
        .required()
        .rule(emailPreset)
        .formField(emailInput)
        .getValue(() => emailInput.getValue())
        .addEvent('blur')
        .compile();

    const passwordConfirmValidator = new Validator()
        .required()
        .rule(new PassRule('Пароли должны совпадать')
            .equal()
            .final())
        .formField(inputPasswordConfirm)
        .getValue(() => inputPasswordConfirm.getValue())
        .addEvent('blur')
        .compile();

    const passwordValidator = new Validator()
        .required()
        .rule(passPreset)
        .formField(inputPassword)
        .getValue(() => inputPassword.getValue())
        .addEvent('blur')
        .compile()
        .onInput(() => {
            passwordConfirmValidator.rule(new PassRule('Пароли должны совпадать')
                .equal(inputPassword.getValue())
                .final()
            );
            if (inputPasswordConfirm.getValue()) {
                passwordConfirmValidator.validate();
            }
        });

    const personalDataValidator = new Validator()
        .required()
        .rule(new BooleanRule().equals(true).final())
        .formField(personalData)
        .getValue(() => personalData.getValue())
        .compile();

    const personalPolicyValidator = new Validator()
        .required()
        .rule(new BooleanRule().equals(true).final())
        .formField(personalPolicy)
        .getValue(() => personalPolicy.getValue())
        .compile();

    return new Gate()
        .addValidator('email', emailValidator)
        .addValidator('password', passwordValidator)
        .addValidator('passwordConfirm', passwordConfirmValidator)
        .addValidator('personalData', personalDataValidator)
        .addValidator('personalPolicy', personalPolicyValidator)
        .onValid(() => registrEmailButton.enable())
        .onInvalid(() => registrEmailButton.disable());
}

function initValidationPhoneFields(componentStore) {
    const {
        phoneInput,
        personalData,
        personalPolicy,
        sendSmsCodeButton
    } = componentStore.all();

    const phoneValidator = new Validator()
        .required()
        .rule(phonePreset)
        .formField(phoneInput)
        .getValue(phoneInput.inputmask)
        .addEvent('blur')
        .withoutMessage()
        .compile();

    const personalDataValidator = new Validator()
        .required()
        .rule(new BooleanRule().equals(true).final())
        .formField(personalData)
        .getValue(() => personalData.getValue())
        .compile();

    const personalPolicyValidator = new Validator()
        .required()
        .rule(new BooleanRule().equals(true).final())
        .formField(personalPolicy)
        .getValue(() => personalPolicy.getValue())
        .compile();

    return new Gate()
        .addValidator('phone', phoneValidator)
        .addValidator('personalData', personalDataValidator)
        .addValidator('personalPolicy', personalPolicyValidator)
        .onValid(() => sendSmsCodeButton.enable())
        .onInvalid(() => sendSmsCodeButton.disable());
}

function updateCodeCardStateFactory(componentStore) {
    const {
        timerP,
        resendSmsCodeLink,
        continueRegisterButton,
        registrToEmailFromCode,
    } = componentStore.all();

    let state = 'await';
    let isTimerWork = false;

    function touch() {
        if (state === 'await') {
            if (isTimerWork) {
                timerP.show();
                resendSmsCodeLink.hide();
                registrToEmailFromCode.hide();
            } else {
                timerP.hide();
                resendSmsCodeLink.show();
                registrToEmailFromCode.show();
            }

            continueRegisterButton.hide();
        } else if (state === 'filled') {
            timerP.hide();
            resendSmsCodeLink.hide();
            continueRegisterButton.show();
            registrToEmailFromCode.hide();
        }
    }

    function setIsTimerWork(isTimerWork_ = true) {
        isTimerWork = isTimerWork_;
    }

    function setState(state_) {
        state = state_;
    }

    return {touch, setIsTimerWork, setState};
}

function sendSmsCode(componentStore, timer, {touch, setIsTimerWork, setState}) {
    let oldPhone = null;

    const {
        phoneInput,
        phoneSpan,
        sendSmsCodeButton,
        codeInput,
        firstStep,
        codeCard,
    } = componentStore.all();

    return async () => {
        const newPhone = phoneInput.getValue();
        const isPhoneChanged = oldPhone !== newPhone;
        const shouldSendSmsCode = isPhoneChanged || !timer.isProcess;

        if (isPhoneChanged) {
            oldPhone = newPhone;
            phoneSpan.textContent = phoneInput.maskedValue;
        }

        if (shouldSendSmsCode) {
            phoneInput.disable();
            sendSmsCodeButton.disable();

            const response = await auth.getRegisterAuthSmsCode(oldPhone);

            phoneInput.enable();
            sendSmsCodeButton.enable();

            if (!response.ok) {
                if (response.statusCode === 422) {
                    phoneInput.error(response.body.errors.phone[0]);
                } else if (response.statusCode === 429) {
                    Toasts.addFromPreset(Toasts.PRESETS.WARNING, {
                        message: 'Слишком много попыток. Попробуйте позже',
                    });
                } else {
                    Toasts.addFromPreset(Toasts.PRESETS.WARNING);
                }

                return;
            }

            timer.stop();
        }

        setIsTimerWork();
        setState('await');
        touch();
        codeInput.setValue('');
        codeInput.error(null);
        timer.start(SMS_SEND_TIMEOUT_SECONDS);
        firstStep.invisible();
        codeCard.visible();
    };
}

function changePhone(componentStore) {
    const {
        codeCard,
        firstStep,
    } = componentStore.all();

    return () => {
        codeCard.invisible();
        firstStep.visible();
    };
}

function codeChanged(componentStore, {touch, setState}) {
    const codeInput = componentStore.getById('codeInput');
    let changed = false;

    return () => {
        const code = codeInput.getValue();

        if (code.length === 4) {
            setState('filled');
            touch();

            changed = true;
        } else if (changed) {
            setState('await');
            touch();

            changed = false;
        }
    };
}

function resendSmsCode(componentStore, timer, {touch, setIsTimerWork}) {
    const {
        phoneInput,
        codeInput,
        resendSmsCodeLink,
    } = componentStore.all();

    return async () => {
        const phone = phoneInput.getValue();

        codeInput.disable();
        resendSmsCodeLink.disable();

        const response = await auth.getRegisterAuthSmsCode(phone);

        codeInput.enable();
        resendSmsCodeLink.enable();

        if (!response.ok) {
            if (response.statusCode === 429) {
                Toasts.addFromPreset(Toasts.PRESETS.WARNING, {
                    message: 'Слишком много попыток. Попробуйте позже',
                });
            } else {
                Toasts.addFromPreset(Toasts.PRESETS.WARNING);
            }

            return;
        }

        codeInput.setValue('');
        codeInput.error(null);
        setIsTimerWork();
        touch();
        timer.start(SMS_SEND_TIMEOUT_SECONDS);
    };
}

function continueRegister(componentStore) {
    const {
        phoneInput,
        codeInput,
        codeCard,
        fieldsCard,
    } = componentStore.all();

    return async () => {
        const phone = phoneInput.getValue();
        const code = codeInput.getValue();

        phoneInput.disable();
        codeInput.disable();

        const response = await auth.verifyRegisterAuthSmsCode(phone, code);

        phoneInput.enable();
        codeInput.enable();

        if (!response.ok) {
            if (response.statusCode === 422) {
                codeInput.error(response.body.errors.auth_code[0]);
            } else if (response.statusCode === 429) {
                Toasts.addFromPreset(Toasts.PRESETS.WARNING, {
                    message: 'Слишком много попыток. Попробуйте позже',
                });
            } else {
                Toasts.addFromPreset(Toasts.PRESETS.WARNING);
            }

            return;
        }

        codeCard.invisible();
        fieldsCard.visible();
    };
}

function registerMapErrors(componentStore) {
    const {
        firstNameInput,
        lastNameInput,
        middleNameInput,
        phoneInputForEmail,
        emailInputForPhone,
        birthdayInput,
    } = componentStore.all();

    return errors => {
        const entries = Object.entries(errors);

        for (const [key, [error]] of entries) {
            let formField = null;

            switch (key) {
                case 'first_name':
                    formField = firstNameInput;
                    break;

                case 'last_name':
                    formField = lastNameInput;
                    break;

                case 'middle_name':
                    formField = middleNameInput;
                    break;

                case 'email':
                    formField = emailInputForPhone;
                    break;

                case 'phone':
                    formField = phoneInputForEmail;
                    break;

                case 'birthday':
                    formField = birthdayInput;
                    break;
            }

            if (formField) {
                formField.error(error);
            }
        }
    };
}

function register(componentStore, updateRegisterState) {
    const {
        phoneInput,
        phoneInputForEmail,
        codeInput,
        firstNameInput,
        lastNameInput,
        middleNameInput,
        emailInput,
        emailInputForPhone,
        inputPassword,
        personalData,
        telegramAdditionConsent,
        birthdayInput,
        companyInput,
        positionInput,
    } = componentStore.all();

    return async () => {
        const registerState = updateRegisterState.getState();
        const mapErrors = registerMapErrors(componentStore);

        const data = {
            type: registerState,
            first_name: firstNameInput.getValue(),
            last_name: lastNameInput.getValue(),
            middle_name: middleNameInput.getValue(),
            agree: personalData.getValue(),
            tg_added_consent: telegramAdditionConsent.getValue(),
            birthday: birthdayInput.maskedValue,
            company: companyInput.getValue(),
            position: positionInput.getValue(),
        };

        if (registerState === 'email') {
            data.phone = phoneInputForEmail.getValue();
            data.email = emailInput.getValue();
            data.password = inputPassword.getValue();
        } else {
            data.phone = phoneInput.getValue();
            data.auth_code = codeInput.getValue();
            data.email = emailInputForPhone.getValue();
        }

        const response = await auth.register(data);

        if (!response.ok) {
            if (response.statusCode === 422) {
                mapErrors(response.body.errors);
            } else {
                Toasts.addFromPreset(Toasts.PRESETS.WARNING);
            }

            return;
        }

        redirect('home');
    };
}

function timerOnChange(componentStore) {
    const timerSecondsSpan = componentStore.getById('timerSecondsSpan');

    return seconds => timerSecondsSpan.textContent = seconds;
}

function timerOnEnd({touch, setIsTimerWork}) {
    return () => {
        setIsTimerWork(false);
        touch();
    };
}

function updateRegisterStateFactory(componentStore, fieldsGate) {
    const {
        fieldsForPhone,
        fieldsForEmail,
    } = componentStore.all();

    let state = 'email';

    function toggleType() {
        switch (state) {
            case 'phone':
                fieldsForEmail.forEach(field => field.hide());
                fieldsForPhone.forEach(field => field.show());
                fieldsGate.enable('emailForPhone');
                fieldsGate.disable('phoneForEmail');

                break;
            case 'email':
                fieldsForEmail.forEach(field => field.show());
                fieldsForPhone.forEach(field => field.hide());
                fieldsGate.enable('phoneForEmail');
                fieldsGate.disable('emailForPhone');

                break;
        }
    }

    function getState() {
        return state;
    }

    function setState(newState) {
        state = newState;
    }

    return {
        toggleType,
        setState,
        getState,
    };
}

function handleChangeTypeRegistr(type, {setState, toggleType}) {
    setState(type);
    toggleType();
}

function checkEmailTaken(componentStore) {
    const {
        emailInput,
        firstStep,
        fieldsCard
    } = componentStore.all();

    return async () => {
        const email = emailInput.getValue();

        const response = await auth.checkEmail(email);

        if (!response.ok) {
            if (response.statusCode === 422) {
                emailInput.error(response.body.errors.email[0]);
            } else {
                Toasts.addFromPreset(Toasts.PRESETS.WARNING);
            }

            return;
        }

        firstStep.invisible();
        fieldsCard.visible();
    }
}

function index() {
    const {componentStore} = bootAuthLayout();
    const fieldsGate = initValidation(componentStore);
    const emailFieldsGate = initValidationEmailFields(componentStore);
    const phoneFieldsGate = initValidationPhoneFields(componentStore);
    const timer = new Timer();
    initMetrics(componentStore);

    const {
        phoneInput,
        sendSmsCodeButton,
        changePhoneLink,
        codeInput,
        resendSmsCodeLink,
        continueRegisterButton,
        registerButton,
        registrToPhone,
        registrToEmail,
        registrToEmailFromCode,
        firstStep,
        codeCard,
        registrEmailButton,
    } = componentStore.all();

    const registerFields = componentStore.getTagsById('registerStepOne');
    const registerFillDataFields = componentStore.getTagsById('registerFillDataFields');

    const updateCodeCardState = updateCodeCardStateFactory(componentStore);
    const updateRegisterState = updateRegisterStateFactory(componentStore, fieldsGate);
    const sendSmsCode_ = sendSmsCode(componentStore, timer, updateCodeCardState);
    const continueRegister_ = continueRegister(componentStore);

    sendSmsCodeButton.on('click', sendSmsCode_);
    changePhoneLink.on('click', changePhone(componentStore));
    codeInput.on('input', codeChanged(componentStore, updateCodeCardState));
    resendSmsCodeLink.on('click', resendSmsCode(componentStore, timer, updateCodeCardState));
    continueRegisterButton.on('click', continueRegister_);
    registerButton.on('click', register(componentStore, updateRegisterState));

    registerFields.forEach((node) => {
        onEnter(node, checkEmailTaken(componentStore), () => emailFieldsGate.isValid);
    })

    registerFillDataFields.forEach((node) => {
        onEnter(node, register(componentStore, updateRegisterState), () => fieldsGate.isValid);
    })

    onEnter(phoneInput, sendSmsCode_, () => phoneFieldsGate.isValid);
    onEnter(codeInput, continueRegister_);

    timer.on('change', timerOnChange(componentStore));
    timer.on('end', timerOnEnd(updateCodeCardState));

    registrToPhone.on('click', () => handleChangeTypeRegistr('phone', updateRegisterState));
    registrToEmail.on('click', () => handleChangeTypeRegistr('email', updateRegisterState));

    registrToEmailFromCode.on('click', () => {
        handleChangeTypeRegistr('email', updateRegisterState);
        firstStep.visible();
        codeCard.invisible();
    });

    registrEmailButton.on('click', checkEmailTaken(componentStore));
}

window.addEventListener('DOMContentLoaded', index);
